import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft, HelpCircle, Download, Calculator } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface PropertyType {
  id: string;
  name: string;
  description: string;
  typicalYield: number;
  typicalGrowth: number;
}

const propertyTypes: PropertyType[] = [
  {
    id: 'residential',
    name: 'Residential Buy-to-Let',
    description: 'Traditional residential rental property',
    typicalYield: 5,
    typicalGrowth: 3
  },
  {
    id: 'hmo',
    name: 'HMO',
    description: 'House in Multiple Occupation',
    typicalYield: 8,
    typicalGrowth: 2.5
  },
  {
    id: 'commercial',
    name: 'Commercial',
    description: 'Office, retail, or industrial property',
    typicalYield: 6,
    typicalGrowth: 2
  }
];

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b'];

export default function PropertyInvestmentCalculator() {
  const [propertyType, setPropertyType] = useState<string>('residential');
  const [purchasePrice, setPurchasePrice] = useState<string>('200000');
  const [deposit, setDeposit] = useState<string>('50000');
  const [rentalIncome, setRentalIncome] = useState<string>('1000');
  const [expenses, setExpenses] = useState<string>('200');
  const [mortgageRate, setMortgageRate] = useState<string>('4.5');
  const [propertyGrowth, setPropertyGrowth] = useState<string>('3');
  const [investmentPeriod, setInvestmentPeriod] = useState(10);
  const [showInfo, setShowInfo] = useState(false);

  const selectedPropertyType = propertyTypes.find(type => type.id === propertyType)!;

  const calculateMortgagePayment = () => {
    const principal = Number(purchasePrice) - Number(deposit);
    const monthlyRate = (Number(mortgageRate) / 100) / 12;
    const numberOfPayments = 25 * 12; // Assuming 25-year mortgage

    if (monthlyRate === 0) return principal / numberOfPayments;

    return principal * 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  };

  const calculateProjection = () => {
    const projection = [];
    const monthlyMortgage = calculateMortgagePayment();
    const monthlyRent = Number(rentalIncome);
    const monthlyExpenses = Number(expenses);
    const annualGrowth = Number(propertyGrowth) / 100;
    
    let propertyValue = Number(purchasePrice);
    let totalRent = 0;
    let totalExpenses = 0;
    let totalMortgage = 0;

    for (let year = 0; year <= investmentPeriod; year++) {
      if (year > 0) {
        propertyValue *= (1 + annualGrowth);
        totalRent += monthlyRent * 12;
        totalExpenses += monthlyExpenses * 12;
        totalMortgage += monthlyMortgage * 12;
      }

      projection.push({
        year,
        propertyValue,
        totalRent,
        totalExpenses,
        totalMortgage,
        netPosition: propertyValue - (Number(purchasePrice) - Number(deposit)) + totalRent - totalExpenses - totalMortgage
      });
    }

    return projection;
  };

  const calculateROI = () => {
    const projection = calculateProjection();
    const finalYear = projection[projection.length - 1];
    const totalInvestment = Number(deposit) + Number(expenses) * 12 * investmentPeriod;
    return (finalYear.netPosition / totalInvestment) * 100;
  };

  const calculateYield = () => {
    const annualRent = Number(rentalIncome) * 12;
    return (annualRent / Number(purchasePrice)) * 100;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadProjection = () => {
    const projection = calculateProjection();
    let csv = 'Property Investment Projection\n\n';
    
    csv += `Property Type,${selectedPropertyType.name}\n`;
    csv += `Purchase Price,${purchasePrice}\n`;
    csv += `Deposit,${deposit}\n`;
    csv += `Monthly Rent,${rentalIncome}\n`;
    csv += `Monthly Expenses,${expenses}\n`;
    csv += `Mortgage Rate,${mortgageRate}%\n`;
    csv += `Growth Rate,${propertyGrowth}%\n\n`;

    csv += 'Year,Property Value,Total Rent,Total Expenses,Total Mortgage,Net Position\n';
    projection.forEach(row => {
      csv += `${row.year},${row.propertyValue},${row.totalRent},${row.totalExpenses},${row.totalMortgage},${row.netPosition}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'property-investment-projection.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const projection = calculateProjection();
  const roi = calculateROI();
  const yield_ = calculateYield();
  const monthlyMortgage = calculateMortgagePayment();
  const monthlyCashflow = Number(rentalIncome) - Number(expenses) - monthlyMortgage;

  // Calculate breakdown for pie chart with percentages
  const totalValue = Number(purchasePrice) + 
                    projection[projection.length - 1].totalRent + 
                    projection[projection.length - 1].totalExpenses + 
                    projection[projection.length - 1].totalMortgage;

  const breakdownData = [
    { 
      name: 'Property Value', 
      value: Number(purchasePrice),
      percentage: (Number(purchasePrice) / totalValue) * 100
    },
    { 
      name: 'Total Rent', 
      value: projection[projection.length - 1].totalRent,
      percentage: (projection[projection.length - 1].totalRent / totalValue) * 100
    },
    { 
      name: 'Total Expenses', 
      value: projection[projection.length - 1].totalExpenses,
      percentage: (projection[projection.length - 1].totalExpenses / totalValue) * 100
    },
    { 
      name: 'Total Mortgage', 
      value: projection[projection.length - 1].totalMortgage,
      percentage: (projection[projection.length - 1].totalMortgage / totalValue) * 100
    }
  ];

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/tools"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Property Investment Calculator
              </h1>
              <p className="text-gray-600">
                Analyze potential property investments and returns
              </p>
            </div>
            <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-brand-700" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Investment Details</h2>
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Property Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Type
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {propertyTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => {
                          setPropertyType(type.id);
                          setPropertyGrowth(type.typicalGrowth.toString());
                        }}
                        className={`p-3 text-sm rounded-lg border-2 transition-all ${
                          propertyType === type.id
                            ? 'border-brand-500 bg-brand-50 text-brand-800'
                            : 'border-gray-200 hover:border-brand-300 text-gray-600'
                        }`}
                      >
                        {type.name}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    {selectedPropertyType.description}
                  </p>
                </div>

                {/* Purchase Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purchase Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                    <input
                      type="number"
                      min="0"
                      value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Deposit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deposit
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                    <input
                      type="number"
                      min="0"
                      value={deposit}
                      onChange={(e) => setDeposit(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    LTV: {Math.round(((Number(purchasePrice) - Number(deposit)) / Number(purchasePrice)) * 100)}%
                  </p>
                </div>

                {/* Monthly Rent */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Rental Income
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                    <input
                      type="number"
                      min="0"
                      value={rentalIncome}
                      onChange={(e) => setRentalIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Monthly Expenses */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Expenses
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                    <input
                      type="number"
                      min="0"
                      value={expenses}
                      onChange={(e) => setExpenses(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Include management fees, insurance, maintenance, etc.
                  </p>
                </div>

                {/* Mortgage Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mortgage Interest Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="15"
                      value={mortgageRate}
                      onChange={(e) => setMortgageRate(e.target.value)}
                      className="w-full pr-8 py-3 px-4 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                </div>

                {/* Property Growth Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Annual Property Growth Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      value={propertyGrowth}
                      onChange={(e) => setPropertyGrowth(e.target.value)}
                      className="w-full pr-8 py-3 px-4 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                </div>

                {/* Investment Period */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Investment Period
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {[5, 10, 15, 20, 25].map((period) => (
                      <button
                        key={period}
                        onClick={() => setInvestmentPeriod(period)}
                        className={`p-3 text-sm rounded-lg border-2 transition-all ${
                          investmentPeriod === period
                            ? 'border-brand-500 bg-brand-50 text-brand-800'
                            : 'border-gray-200 hover:border-brand-300 text-gray-600'
                        }`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Results Summary */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Summary</h2>
                <button
                  onClick={downloadProjection}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Monthly Cashflow</p>
                  <p className={`text-2xl font-bold ${monthlyCashflow >= 0 ? 'text-brand-700' : 'text-red-600'}`}>
                    {formatCurrency(monthlyCashflow)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Rental Yield</p>
                  <p className="text-2xl font-bold text-brand-700">
                    {yield_.toFixed(1)}%
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Total ROI ({investmentPeriod} years)</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {roi.toFixed(1)}%
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Monthly Mortgage</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(monthlyMortgage)}
                  </p>
                </div>
              </div>

              <div className="mt-6 h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={breakdownData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                    >
                      {breakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string, props: any) => [
                        `${formatCurrency(value)} (${(props.payload.percentage).toFixed(1)}%)`,
                        name
                      ]}
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '8px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Projection Charts */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Investment Projection
              </h2>

              <div className="h-[400px] mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={projection}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="year"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                    />
                    <YAxis
                      tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '8px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="propertyValue"
                      name="Property Value"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="netPosition"
                      name="Net Position"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                      <th className="pb-3 font-medium">Year</th>
                      <th className="pb-3 font-medium">Property Value</th>
                      <th className="pb-3 font-medium">Total Rent</th>
                      <th className="pb-3 font-medium">Net Position</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {projection.map((row) => (
                      <tr key={row.year} className="border-b border-gray-100">
                        <td className="py-3">{row.year}</td>
                        <td className="py-3">{formatCurrency(row.propertyValue)}</td>
                        <td className="py-3">{formatCurrency(row.totalRent)}</td>
                        <td className="py-3">{formatCurrency(row.netPosition)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-brand-50 rounded-xl p-6">
              <h3 className="font-semibold text-brand-900 mb-2">Investment Tips</h3>
              <ul className="text-sm text-brand-800 space-y-2">
                <li>Consider location and potential for capital growth</li>
                <li>Research local rental demand and achievable rents</li>
                <li>Factor in all costs including maintenance and void periods</li>
                <li>Review mortgage options and interest rates carefully</li>
                <li>Consider tax implications and seek professional advice</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}